import type { MonitorArrival, MonitorPickup } from '@/apis/monitor';
import {
  shipmentPosition,
  type NeedsAttentionItem,
  type StreamShipment,
} from '../Home/map/needsAttention';
import { formatBoardTime, formatDestination, landingTime } from './arrivalRows';

/**
 * Upcoming arrivals as rotation items, earliest first. Only shipments the map
 * has a position for: the camera needs somewhere to fly. Delivered rows stay
 * on the arrivals board only.
 */
const positionsOf = (shipments: StreamShipment[]) => {
  const positions = new Map<string, { lat: number; lng: number }>();
  shipments.forEach((s) => {
    const position = shipmentPosition(s);
    if (position) positions.set(String(s.shipment_id), position);
  });
  return positions;
};

export const buildArrivingItems = (
  arrivals: MonitorArrival[] | null,
  shipments: StreamShipment[]
): NeedsAttentionItem[] => {
  const positions = positionsOf(shipments);
  return (arrivals ?? [])
    .filter((a) => a.status !== 'delivered' && positions.has(String(a.id)))
    .map((a) => {
      const at = positions.get(String(a.id)) as { lat: number; lng: number };
      return {
        shipmentId: String(a.id),
        referenceNumber: a.reference_number || String(a.id),
        severity: 'arriving' as const,
        reasons: [
          `Arrives ${formatBoardTime(landingTime(a))} · ${formatDestination(a)}`,
        ],
        score: 0,
        lat: at.lat,
        lng: at.lng,
      };
    });
};

/** Where a pickup is: "Houston, TX", else the place's name. */
const formatOrigin = (pickup: MonitorPickup): string => {
  const { city, state, name } = pickup.origin;
  return [city, state].filter(Boolean).join(', ') || name || '—';
};

/**
 * Shipments due to be picked up soon as rotation items, earliest first. Only
 * those the map has a position for, like arrivals.
 */
export const buildPickupItems = (
  pickups: MonitorPickup[] | null,
  shipments: StreamShipment[]
): NeedsAttentionItem[] => {
  const positions = positionsOf(shipments);
  return (pickups ?? [])
    .filter((p) => positions.has(String(p.id)))
    .map((p) => {
      const at = positions.get(String(p.id)) as { lat: number; lng: number };
      return {
        shipmentId: String(p.id),
        referenceNumber: p.reference_number || String(p.id),
        severity: 'pickup' as const,
        reasons: [
          `Pickup ${formatBoardTime(p.scheduled_pickup_date)} · ${formatOrigin(p)}`,
        ],
        score: 0,
        lat: at.lat,
        lng: at.lng,
      };
    });
};
